import { CLIENT_REPORTABLE_METRICS } from '../conventions';
import type { ClientMetricName } from '../conventions';

const REPORT_METRIC_ENDPOINT = '/api/report-metric';
const MAX_BODY_BYTES = 4096;
const MAX_TAG_VALUE_LENGTH = 64;

export interface IncrementClientMetricParams {
  name: ClientMetricName;
  tags?: Record<string, string>;
}

function truncateTagValue(value: string): string {
  return value.length > MAX_TAG_VALUE_LENGTH ? value.slice(0, MAX_TAG_VALUE_LENGTH) : value;
}

function buildBody(params: IncrementClientMetricParams): string {
  const tags = params.tags
    ? Object.fromEntries(
        Object.entries(params.tags).map(([key, value]) => [key, truncateTagValue(value)]),
      )
    : undefined;

  return JSON.stringify({
    name: params.name,
    ...(tags && Object.keys(tags).length > 0 ? { tags } : {}),
  });
}

/**
 * Relays an allowlisted counter increment from the browser to the server via beacon.
 * Internal transport — import typed helpers from `@highlight-cp/metrics/client` instead.
 */
export function incrementClientMetric(params: IncrementClientMetricParams): void {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'production') {
    return;
  }

  if (!CLIENT_REPORTABLE_METRICS.has(params.name)) {
    return;
  }

  try {
    const body = buildBody(params);

    if (body.length > MAX_BODY_BYTES) {
      return;
    }

    if (navigator.sendBeacon) {
      navigator.sendBeacon(REPORT_METRIC_ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }

    void fetch(REPORT_METRIC_ENDPOINT, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    });
  } catch {
    // Swallow transport errors — metrics must not break the app.
  }
}
